/**
 * Providers that need the router (inside the root route): session, theme, upsell, dock and the
 * SSE stream. `main.tsx` wraps the router with the QueryClient, i18n and auth providers.
 */
import { DockProvider, Dock } from '@oex/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, type ReactNode } from 'react';
import { API_URL, tokenStore } from './api';
import { useAuth } from './auth';
import { MessagingDock } from './messaging-dock';
import { runtime } from './runtime';
import { SessionProvider, useSession } from './session';
import { playPing } from './sound';
import { useSseInvalidation } from './sse';
import { ThemeProvider, type ThemePreference } from './theme';
import type { UiSettings } from './ui-settings';
import { UpsellProvider, useUpsell } from './upsell';

function RuntimeBridge() {
  const { activeOrgId, status, me } = useSession();
  const upsell = useUpsell();
  const queryClient = useQueryClient();
  runtime.orgId = activeOrgId;
  runtime.openUpsell = upsell.openFromProblem;
  // Latest settings for the SSE callback (captured once; a ref keeps it fresh without reconnecting).
  const settings = useRef<UiSettings>({});
  settings.current = (me?.user.uiSettings ?? {}) as UiSettings;
  useSseInvalidation(queryClient, {
    enabled: status === 'ready',
    url: `${API_URL}/v1/me/stream`,
    getToken: tokenStore.get,
    getOrgId: () => runtime.orgId,
    onMessage: (m) => {
      if (m.event !== 'message' && m.event !== 'thread') return;
      const s = settings.current;
      if (!s.messagingSound) return;
      const threadId = (m.data as { threadId?: string } | null)?.threadId;
      if (threadId && (s.mutedThreadIds ?? []).includes(threadId)) return;
      playPing();
    },
  });
  useEffect(
    () => () => {
      runtime.openUpsell = null;
    },
    [],
  );
  return null;
}

function Themed({ children }: { children: ReactNode }) {
  const { me } = useSession();
  return (
    <ThemeProvider initial={(me?.user.theme as ThemePreference | undefined) ?? null}>
      {children}
    </ThemeProvider>
  );
}

export function AppProviders({ children }: { children: ReactNode }) {
  const { token } = useAuth();
  return (
    <SessionProvider token={token}>
      <Themed>
        <UpsellProvider>
          <DockProvider>
            <RuntimeBridge />
            {children}
            <MessagingDock />
            <Dock />
          </DockProvider>
        </UpsellProvider>
      </Themed>
    </SessionProvider>
  );
}
